import { useState } from 'react';
import { TextField as MuiTextField, TextFieldProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import InputMask from '@mona-health/react-input-mask';

export type FormTextFieldProps = TextFieldProps & {
  inputMask?: string;
  maskChar?: string | null;
  readOnly?: boolean;
};

export default function FormTextField({
  inputMask,
  maskChar = null,
  readOnly,
  InputProps = {},
  InputLabelProps,
  sx = {},
  value,
  onChange,
  onFocus,
  onBlur,
  disabled,
  ...props
}: FormTextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const shouldShrink = Boolean(value) || isFocused || props.type === 'date' || InputLabelProps?.shrink;

  const inputLabelProps = {
    ...InputLabelProps,
    shrink: shouldShrink,
  };

  const enhancedInputProps = readOnly !== undefined
    ? { ...InputProps, readOnly }
    : InputProps;

  const readOnlyStyles: SxProps<Theme> = readOnly ? {
    '& .MuiOutlinedInput-root': {
      cursor: 'default',
      backgroundColor: '#f5f5f5',
    },
    '& .MuiInputBase-input': {
      cursor: 'default !important',
      backgroundColor: 'transparent !important',
      pointerEvents: 'none',
    },
    '& .MuiInputBase-inputMultiline': {
      cursor: 'default !important',
      backgroundColor: 'transparent !important',
      pointerEvents: 'none',
    },
  } : {};

  const enhancedSx: SxProps<Theme> | undefined = readOnly
    ? [...(Array.isArray(sx) ? sx : [sx]), readOnlyStyles].filter(Boolean) as SxProps<Theme>
    : sx;

  const handleFocus: TextFieldProps['onFocus'] = (event) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur: TextFieldProps['onBlur'] = (event) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  if (!inputMask) {
    return (
      <MuiTextField
        {...props}
        value={value}
        onChange={onChange}
        disabled={disabled}
        InputProps={enhancedInputProps}
        InputLabelProps={inputLabelProps}
        sx={enhancedSx}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  }

  return (
    <InputMask
      mask={inputMask}
      value={value != null ? String(value) : ''}
      onChange={onChange}
      disabled={disabled}
      onFocus={handleFocus}
      onBlur={handleBlur}
      maskPlaceholder={maskChar}
    >
      <MuiTextField
        {...props}
        value={undefined}
        onChange={undefined}
        onFocus={undefined}
        onBlur={undefined}
        disabled={undefined}
        InputProps={enhancedInputProps}
        InputLabelProps={inputLabelProps}
        sx={enhancedSx}
      />
    </InputMask>
  );
}
